"use client"

import React, { createContext, useContext, useState, useEffect } from "react"
import "@/lib/zod-error-map"

interface UIContextType {
    isSidebarCollapsed: boolean
    toggleSidebar: () => void
    setSidebarCollapsed: (value: boolean) => void
    atendimentoMode: 'hidden' | 'full' | 'pip' | 'compact'
    setAtendimentoMode: (mode: 'hidden' | 'full' | 'pip' | 'compact') => void
    isMobileMenuOpen: boolean
    setMobileMenuOpen: (open: boolean) => void
}

const UIContext = createContext<UIContextType | undefined>(undefined)

export function UIProvider({ children }: { children: React.ReactNode }) {
    // Inicialmente falso, pode-se adicionar persistência em localStorage depois
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
    const [atendimentoMode, setAtendimentoMode] = useState<'hidden' | 'full' | 'pip' | 'compact'>('hidden')
    // Mobile menu state - EPHEMERAL (não persistir no localStorage)
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false)

    // Persistir estado no localStorage (opcional, bom para UX)
    useEffect(() => {
        const savedSidebar = localStorage.getItem("sidebarCollapsed")
        if (savedSidebar) {
            setIsSidebarCollapsed(JSON.parse(savedSidebar))
        }

        const savedMode = localStorage.getItem("atendimentoMode")
        if (savedMode) {
            setAtendimentoMode(JSON.parse(savedMode))
        }
    }, [])

    const toggleSidebar = () => {
        setIsSidebarCollapsed((prev) => {
            const newState = !prev
            localStorage.setItem("sidebarCollapsed", JSON.stringify(newState))
            return newState
        })
    }

    const setSidebarCollapsed = (value: boolean) => {
        setIsSidebarCollapsed(value)
        localStorage.setItem("sidebarCollapsed", JSON.stringify(value))
    }

    const handleSetAtendimentoMode = (mode: 'hidden' | 'full' | 'pip' | 'compact') => {
        setAtendimentoMode(mode)
        localStorage.setItem("atendimentoMode", JSON.stringify(mode))
    }

    return (
        <UIContext.Provider value={{
            isSidebarCollapsed,
            toggleSidebar,
            setSidebarCollapsed,
            atendimentoMode,
            setAtendimentoMode: handleSetAtendimentoMode,
            isMobileMenuOpen,
            setMobileMenuOpen
        }}>
            {children}
        </UIContext.Provider>
    )
}

export function useUI() {
    const context = useContext(UIContext)
    if (context === undefined) {
        throw new Error("useUI must be used within a UIProvider")
    }
    return context
}
